/**
 * PHARMAKON — the one client source of the stock-alert counts (ux/01 §6.2
 * « Its count comes from the one shared source », OP-F-23; cross-module §3.7;
 * WP-18, X XWP-15). `GET /stock/alertes/compteurs` feeds the topbar entries
 * AND the views strip of Stock & lots, so the two can never disagree. Scoped
 * to the pharmacy it was read for: a session on another tenant never sees the
 * previous one's numbers. Nothing persisted, never polled on a timer: a
 * `stock` topic event naming `stock:alertes` / `stock:lots` re-reads it
 * (components/layout/topbar/StockAlertEntries.tsx), and so does window focus
 * on a stale value.
 */
import { create } from "zustand";
import { stockApi } from "@/services/index.js";
import { resumeCompteurs } from "@/lib/stock-alerts.js";
import { STOCK_ALERTS_STALE_MS } from "@/config/ui.config.js";

let inflight = null;
/** A write was announced while a read was running: that read may predate it — read once more. */
let relire = false;

export const useStockAlertStore = create((set, get) => ({
  pharmacieId: null,
  summary: null,
  loadedAt: 0,
  loading: false,
  error: null,

  /**
   * Read now (deduplicated while a read for the same pharmacy is running). `apresEcriture` — the
   * call follows a stock write (a `stock` topic event, a correction just saved): a read already
   * running may have started before that write, so one more read follows it (never more than one).
   */
  refresh: async (pharmacieId, { apresEcriture = false } = {}) => {
    if (!pharmacieId) return null;
    if (inflight && inflight.pharmacieId === pharmacieId) {
      if (apresEcriture) relire = true;
      return inflight.promise;
    }
    if (get().pharmacieId !== pharmacieId) set({ pharmacieId, summary: null, loadedAt: 0, error: null });
    set({ loading: true });
    const promise = stockApi
      .compteurs()
      .then((r) => {
        if (get().pharmacieId !== pharmacieId) return null; // tenant changed meanwhile
        const summary = resumeCompteurs(r?.data ?? r);
        set({ summary, loadedAt: Date.now(), loading: false, error: null });
        return summary;
      })
      .catch((e) => {
        if (get().pharmacieId === pharmacieId) {
          set({ loading: false, error: e?.response?.data?.message ?? e?.message ?? "Erreur" });
        }
        return null;
      })
      .finally(() => {
        if (inflight?.promise === promise) inflight = null;
        if (relire && get().pharmacieId === pharmacieId) {
          relire = false;
          get().refresh(pharmacieId);
        }
      });
    inflight = { pharmacieId, promise };
    return promise;
  },

  /** Read only when there is no fresh value for this pharmacy. */
  ensure: (pharmacieId, maxAgeMs = STOCK_ALERTS_STALE_MS) => {
    const s = get();
    if (!pharmacieId) return Promise.resolve(null);
    if (s.pharmacieId === pharmacieId && s.summary && Date.now() - s.loadedAt < maxAgeMs) {
      return Promise.resolve(s.summary);
    }
    return s.refresh(pharmacieId);
  },

  reset: () => {
    relire = false;
    set({ pharmacieId: null, summary: null, loadedAt: 0, loading: false, error: null });
  },
}));

export default useStockAlertStore;
